if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        // Forstå 2026-10-09: husk hvilket valg kunden ændrede (til forstaaAutoSelect)
        const forstaaSelects = event.target.closest('variant-selects');
        const forstaaGroups = forstaaSelects ? Array.from(forstaaSelects.querySelectorAll('.product-form__input')) : [];
        this.forstaaChangedIndex = forstaaGroups.findIndex((group) => group.contains(event.target));
        if (!event.forstaaAuto) this.forstaaAutoFixed = false;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';
        // Forstå 2026-10-09: lås hovedknappen mens det nye valg hentes (ellers kan et hurtigt klik lægge den forrige variant i kurven)
        this.toggleAddButton(true, '', false);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          // Forstå 2026-10-09: svaret er ikke en sektion (fx 429-fejlside) -> tilbage til sidste gyldige valg, knapper aktive
          if (!html.querySelector('variant-selects')) {
            this.forstaaRestoreSelection();
            return;
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            if (html.querySelector('variant-selects') && this.forstaaAutoSelect()) return;
            this.setUnavailable();
            document.dispatchEvent(
              new CustomEvent('forstaa:variant-unavailable', { detail: { sectionId: this.sectionId } })
            );
            return;
          }

          this.forstaaLastVariant = variant;
          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );
          // Forstå 2026-09-23: behold serverens knaptekst (fx Forudbestil) ved variantskift
          const addButtonUpdatedText = addButtonUpdated && !addButtonUpdated.hasAttribute('disabled') && addButtonUpdated.querySelector('span');
          const addButtonCurrentText = document.querySelector(`#product-form-${this.dataset.section} [name="add"] > span`);
          if (addButtonUpdatedText && addButtonCurrentText) addButtonCurrentText.textContent = addButtonUpdatedText.textContent.trim();

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      // Forstå 2026-10-09: findes kundens kombination ikke (fx 2 skuffer under 180 cm), skiftes et SENERE valg
      // til den første værdi der findes sammen med kundens valg. Det valg kunden selv ændrede røres aldrig,
      // der skiftes højst én gang pr. kundeklik, kun i valg markeret data-forstaa-auto (i dag "Skuffe"), og kun på et
      // rigtigt sektionssvar. Kræver data-option-exists fra product-variant-options.liquid.
      forstaaAutoSelect() {
        if (this.forstaaAutoFixed) return false;
        // temaet indsætter de nye valg FØR de gamle og skjuler de gamle i 500 ms (HTMLUpdateUtility.viewTransition)
        const current = Array.from(this.querySelectorAll('variant-selects')).find((element) => element.style.display !== 'none');
        if (!current) return false;
        const groups = Array.from(current.querySelectorAll('.product-form__input'));
        const from = typeof this.forstaaChangedIndex === 'number' ? this.forstaaChangedIndex : -1;
        if (from < 0) return false;
        for (let i = from + 1; i < groups.length; i++) {
          if (!groups[i].hasAttribute('data-forstaa-auto')) continue;
          const select = groups[i].querySelector('select');
          const items = select ? Array.from(select.options) : Array.from(groups[i].querySelectorAll('input[type="radio"]'));
          const chosen = select ? select.selectedOptions[0] : items.find((item) => item.checked);
          if (!chosen || chosen.dataset.optionExists !== 'false') continue;
          const next =
            items.find((item) => item.dataset.optionExists === 'true' && !item.classList.contains('disabled')) ||
            items.find((item) => item.dataset.optionExists === 'true');
          if (!next) continue;
          this.forstaaAutoFixed = true;
          if (select) {
            select.value = next.value;
          } else {
            next.checked = true;
          }
          const forstaaEvent = new Event('change', { bubbles: true });
          forstaaEvent.forstaaAuto = true;
          (select || next).dispatchEvent(forstaaEvent);
          return true;
        }
        return false;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        // Forstå 2026-10-09: hænger svaret i 15 sek., afbrydes det og siden går tilbage til sidste gyldige valg
        clearTimeout(this.forstaaTimer);
        this.forstaaTimedOut = false;
        const forstaaController = this.abortController;
        this.forstaaTimer = setTimeout(() => {
          this.forstaaTimedOut = true;
          forstaaController.abort();
        }, 15000);

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (this.abortController !== forstaaController) return; // Forstå: et forældet svar må ikke overskrive et nyere valg
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (this.abortController !== forstaaController) return; // en nyere forespørgsel har overtaget
            if (error.name === 'AbortError' && !this.forstaaTimedOut) {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              this.forstaaRestoreSelection();
            }
          })
          .finally(() => {
            if (this.abortController === forstaaController) clearTimeout(this.forstaaTimer);
          });
      }

      // Forstå 2026-10-09: et valg kunne ikke hentes (netværksfejl, fejlside, timeout). Valgene sættes tilbage til sidste
      // gyldige variant, og knapperne følger den, så kunden hverken sidder fast eller kan købe en anden seng end den viste.
      forstaaRestoreSelection() {
        const variant = this.forstaaLastVariant || this.currentVariant;
        const current = Array.from(this.querySelectorAll('variant-selects')).find((element) => element.style.display !== 'none');
        if (variant && current && Array.isArray(variant.options)) {
          Array.from(current.querySelectorAll('.product-form__input')).forEach((group, index) => {
            const value = variant.options[index];
            if (value === undefined) return;
            const select = group.querySelector('select');
            if (select) {
              select.value = value;
              Array.from(select.options).forEach((option) => option.toggleAttribute('selected', option.value === value));
            } else {
              group.querySelectorAll('input[type="radio"]').forEach((input) => {
                input.checked = input.value === value;
              });
            }
            const label = group.querySelector('[data-selected-swatch-value]');
            if (label) label.textContent = value;
          });
        }
        if (variant) {
          // formularens variant-id og den viste pris følger de gendannede valg (en ugyldig kombination tømmer id og skjuler pris)
          this.updateVariantInputs(variant.id);
          document.getElementById(`price-${this.dataset.section}`)?.classList.remove('hidden');
        }
        const addButton = document.querySelector(`#product-form-${this.dataset.section} [name="add"]`);
        if (addButton) {
          if (variant && variant.available) {
            addButton.removeAttribute('disabled');
            const span = addButton.querySelector('span');
            if (span && span.textContent.trim() === this.decoded(FoxTheme.variantStrings.unavailable).trim()) {
              span.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
            }
          } else {
            addButton.setAttribute('disabled', 'disabled');
          }
        }
        document.dispatchEvent(
          new CustomEvent('forstaa:variant-restored', { detail: { sectionId: this.sectionId, variant } })
        );
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}
